import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { PolygonElement } from '../elements/polygon-element';
import { PointElement } from '../elements/point-element';
import { LevelDesignerElementBase } from '../elements/element-base';

export class MoveManager extends ManagerBase {
    private isDragging: boolean = false;
    private dragStartX: number = 0;
    private dragStartY: number = 0;
    private initialElementPositions: Map<LevelDesignerElementBase, { x: number, y: number }[] | { x: number, y: number }> = new Map();
    private initialX: number = 0;
    private initialY: number = 0;
    private hasMoved: boolean = false;
    private readonly KEYBOARD_MOVE_DISTANCE = 0.1; // 0.1m movement for arrow keys
    private readonly SNAP_THRESHOLD = 0.1; // 20cm snap threshold
    public isSnappingEnabled = true; // Can be toggled
    private snapLines: { x?: number, y?: number } = {};


    constructor(levelDesigner: LevelDesignerComponent, canvas: HTMLCanvasElement) {
        super(levelDesigner, canvas);
    }

    public override handleMouseDown(event: MouseEvent): MouseEventResult | undefined {
        if (event.button !== 0) return undefined;

        const { x, y } = this.getMousePosition(event);
        if (this.levelDesigner.selectedElements.length === 0) return undefined;
        if(this.levelDesigner.selectedElements.some(element => element.isLocked())) return undefined;

        if (this.isOverMovableElement(x, y)) {
            this.isDragging = true;
            this.hasMoved = false;  // Reset hasMoved flag when starting new drag
            this.dragStartX = x;
            this.dragStartY = y;

            // Store initial positions of all selected elements
            this.initialElementPositions.clear();
            this.levelDesigner.selectedElements.forEach(element => {
                if (element instanceof PolygonElement) {
                    this.initialElementPositions.set(element, [...element.getPoints()]);
                } else if (element instanceof PointElement) {
                    this.initialElementPositions.set(element, { x: element.x, y: element.y });
                }
            });

            return { cursor: 'move', stopPropagation: !event.shiftKey, redraw: false };
        }
        return undefined;
    }

    public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
        const { x, y } = this.getMousePosition(event);

        if (this.isDragging) {
            const deltaX = x - this.dragStartX;
            const deltaY = y - this.dragStartY;

            this.moveSelectedElements(deltaX, deltaY);

            this.dragStartX = x;
            this.dragStartY = y;

            if (!this.hasMoved) {
                const totalDeltaX = x - this.initialX;
                const totalDeltaY = y - this.initialY;
                if (Math.abs(totalDeltaX) > 1 || Math.abs(totalDeltaY) > 1) {
                    this.hasMoved = true;
                }
            }
            return { cursor: 'move', stopPropagation: true, redraw: true };
        } else if (this.isOverMovableElement(x, y)) {
            return { cursor: 'move', stopPropagation: false, redraw: false };
        }

        return undefined;
    }

    public override handleMouseUp(event: MouseEvent): MouseEventResult | undefined {
        if (!this.isDragging || !this.hasMoved) {
            this.isDragging = false;
            return undefined;
        }

        if (this.levelDesigner.selectedElements.length > 0) {
            let hasValidationErrors = false;

            // Check position validity for all dragged elements
            this.levelDesigner.selectedElements.forEach(element => {
                const validationResult = element.checkPositionValid(this.levelDesigner.elements);
                if (validationResult.errors.length > 0) {
                    // Show validation errors to the user via toast
                    validationResult.errors.forEach(error => {
                        this.levelDesigner.toastService.error(error, 'Position Error');
                    });
                    hasValidationErrors = true;
                }
            });

            // If there were validation errors, revert positions
            if (hasValidationErrors) {
                this.levelDesigner.selectedElements.forEach(element => {
                    const originalPosition = this.initialElementPositions.get(element);
                    if (!originalPosition) return;

                    if (element instanceof PolygonElement && Array.isArray(originalPosition)) {
                        const currentPoints = element.getPoints();
                        const dx = originalPosition[0].x - currentPoints[0].x;
                        const dy = originalPosition[0].y - currentPoints[0].y;
                        element.move(dx, dy);
                    } else if (element instanceof PointElement && !Array.isArray(originalPosition)) {
                        const dx = originalPosition.x - element.x;
                        const dy = originalPosition.y - element.y;
                        element.move(dx, dy);
                    }
                });
            }

            this.isDragging = false;
            this.initialElementPositions.clear();
            this.levelDesigner.saveState();
            return { redraw: true, cursor: 'default', stopPropagation: true };
        }
        
        this.isDragging = false;
        return undefined;
    }

    public override handleKeyDown(event: KeyboardEvent): void {
        if (this.levelDesigner.selectedElements.length === 0) return;
        if (this.levelDesigner.selectedElements.some(element => element.isLocked())) return;

        let deltaX = 0;
        let deltaY = 0;

        switch (event.key) {
            case 'ArrowLeft':
                deltaX = -this.KEYBOARD_MOVE_DISTANCE;
                break;
            case 'ArrowRight':
                deltaX = this.KEYBOARD_MOVE_DISTANCE;
                break;
            case 'ArrowUp':
                deltaY = -this.KEYBOARD_MOVE_DISTANCE;
                break;
            case 'ArrowDown':
                deltaY = this.KEYBOARD_MOVE_DISTANCE;
                break;
            default:
                return;
        }

        if (deltaX !== 0 || deltaY !== 0) {
            this.moveSelectedElements(deltaX, deltaY);
            this.levelDesigner.redraw();
            this.levelDesigner.saveState();
            event.preventDefault(); // Prevent page scrolling
        }
    }

    private moveSelectedElements(deltaX: number, deltaY: number) {
        const firstElement = this.levelDesigner.selectedElements[0];
        let targetX: number, targetY: number;
        
        // Get the target position based on element type
        if (firstElement instanceof PolygonElement) {
            const points = firstElement.getPoints();
            targetX = points[0].x + deltaX;
            targetY = points[0].y + deltaY;
        } else {
            targetX = (firstElement as PointElement).x + deltaX;
            targetY = (firstElement as PointElement).y + deltaY;
        }

        // Find snap points if snapping is enabled
        if (this.isSnappingEnabled) {
            this.snapLines = {};
            const otherElements = this.levelDesigner.elements.filter(
                el => !this.levelDesigner.selectedElements.includes(el) && el instanceof PolygonElement
            ) as PolygonElement[];

            // Collect all points from polygon elements
            const snapPoints: { x: number, y: number }[] = [];
            for (const element of otherElements) {
                snapPoints.push(...element.getPoints());
            }

            // Get all points of the first selected element after applying the initial delta
            let selectedPoints: { x: number, y: number }[] = [];
            if (firstElement instanceof PolygonElement) {
                selectedPoints = firstElement.getPoints().map(p => ({
                    x: p.x + deltaX,
                    y: p.y + deltaY
                }));
            } else {
                selectedPoints = [{
                    x: (firstElement as PointElement).x + deltaX,
                    y: (firstElement as PointElement).y + deltaY
                }];
            }

            // Find the closest point pair and their distance
            let minDistance = Infinity;
            let closestSelectedPoint: { x: number, y: number } | null = null;
            let closestSnapPoint: { x: number, y: number } | null = null;

            for (const selectedPoint of selectedPoints) {
                for (const snapPoint of snapPoints) {
                    const distance = Math.sqrt(
                        Math.pow(selectedPoint.x - snapPoint.x, 2) + 
                        Math.pow(selectedPoint.y - snapPoint.y, 2)
                    );
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestSelectedPoint = selectedPoint;
                        closestSnapPoint = snapPoint;
                    }
                }
            }

            // Apply snapping if within threshold
            if (closestSelectedPoint && closestSnapPoint && minDistance < this.SNAP_THRESHOLD) {
                // Calculate the adjustment needed to align the closest points
                const adjustmentX = closestSnapPoint.x - closestSelectedPoint.x;
                const adjustmentY = closestSnapPoint.y - closestSelectedPoint.y;

                // Apply the adjustment to the delta
                deltaX += adjustmentX;
                deltaY += adjustmentY;

                // Set snap lines
                this.snapLines.x = closestSnapPoint.x;
                this.snapLines.y = closestSnapPoint.y;
            }
        }

        // Track which elements have been moved to avoid moving them twice
        const movedElements = new Set<LevelDesignerElementBase>();

        // Move all selected elements with the adjusted deltas
        for (const element of this.levelDesigner.selectedElements) {
            if (!movedElements.has(element)) {
                element.move(deltaX, deltaY);
                movedElements.add(element);

                // If this is a polygon element, move its children
                if (element instanceof PolygonElement) {
                    // First check if any children are selected
                    const selectedChildren = element.elements.filter(child => 
                        this.levelDesigner.selectedElements.includes(child)
                    );

                    // Move unselected children with the parent
                    for (const child of element.elements) {
                        if (!movedElements.has(child) && !selectedChildren.includes(child)) {
                            child.move(deltaX, deltaY);
                            movedElements.add(child);
                        }
                    }
                }
            }
        }

        // Trigger redraw to show snap lines
        this.levelDesigner.redraw();
    }

    private isOverMovableElement(x: number, y: number): boolean {
        // Get all elements after the last selected element in the array
        const allElements = this.levelDesigner.elements;
        const lastSelectedIndex = Math.max(...this.levelDesigner.selectedElements.map(el => 
            allElements.indexOf(el)));
        const higherElements = allElements.slice(lastSelectedIndex + 1);

        // If any higher element is under the cursor, return false
        if (higherElements.some(element => element.isPointInside(x, y))) {
            return false;
        }

        // Original checks for selected elements
        if (this.levelDesigner.selectedElements.length === 1) {
            return this.levelDesigner.selectedElements[0].isPointInside(x, y);
        } else if (this.levelDesigner.selectedElements.length > 1) {
            return this.levelDesigner.selectedElements.some(element => 
                element.isPointInside(x, y));
        }
        return false;
    }

    public OnDraw(): void {
        // No specific drawing for the move manager
    }
}
